import React, { useEffect, useRef } from 'react';

/**
 * GAIL Technology unified theme toggle - animated sun/moon
 * Consistent across all GAIL apps
 */
function ThemeToggle({ theme, onToggle }) {
  const isDark = theme === 'dark';
  const sunRef = useRef(null);
  const moonRef = useRef(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const sun = sunRef.current;
    const moon = moonRef.current;
    if (!sun || !moon) return;

    if (isInitialMount.current) {
      // Initial state - no animation
      if (isDark) {
        moon.style.opacity = '1';
        moon.style.transform = 'translateY(-50%) scale(1)';
        sun.style.opacity = '0';
        sun.style.transform = 'translateY(-50%) scale(0.5)';
      } else {
        sun.style.opacity = '1';
        sun.style.transform = 'translateY(-50%) scale(1)';
        moon.style.opacity = '0';
        moon.style.transform = 'translateY(-50%) scale(0.5)';
      }
      isInitialMount.current = false;
    }
  }, [isDark]);

  const handleToggle = () => {
    const sun = sunRef.current;
    const moon = moonRef.current;
    if (!sun || !moon) return;

    const revealIcon = isDark ? sun : moon;
    const hideIcon = isDark ? moon : sun;

    // Animate icons
    setTimeout(() => {
      revealIcon.style.opacity = '1';
      revealIcon.style.transform = 'translateY(-50%) scale(1)';
      revealIcon.style.transition = 'all 0.5s cubic-bezier(0.68, -0.15, 0.32, 1.15)';
    }, 150);

    setTimeout(() => {
      hideIcon.style.opacity = '0';
      hideIcon.style.transform = 'translateY(-50%) scale(0.5)';
      hideIcon.style.transition = 'all 0.1s ease';
    }, 200);

    onToggle();
  };

  return (
    <button
      onClick={handleToggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 1000,
        width: 60,
        height: 32,
        borderRadius: 16,
        border: 'none',
        cursor: 'pointer',
        padding: 3,
        background: isDark ? '#2d3748' : '#ed8936',
        transition: 'background 0.4s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        outline: 'none',
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
      }}
    >
      {/* Sun icon */}
      <svg
        ref={sunRef}
        viewBox="0 0 24 24"
        fill="white"
        style={{
          position: 'absolute',
          left: 8,
          top: '50%',
          transform: 'translateY(-50%) scale(0.5)',
          width: 18,
          height: 18,
          zIndex: 2,
          opacity: 0,
          transition: 'all 0.5s cubic-bezier(0.68, -0.15, 0.32, 1.15)',
        }}
      >
        <circle cx="12" cy="12" r="4"/>
        <path
          d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
      </svg>

      {/* Toggle thumb */}
      <div
        style={{
          position: 'absolute',
          width: 26,
          height: 26,
          borderRadius: '50%',
          transition: 'all 0.52s cubic-bezier(0.4, 0, 0.2, 1)',
          left: isDark ? 3 : 31,
          top: 3,
          background: isDark ? '#63b3a6' : '#fcd34d',
        }}
      />

      {/* Moon icon */}
      <svg
        ref={moonRef}
        viewBox="0 0 24 24"
        fill="white"
        style={{
          position: 'absolute',
          right: 7,
          top: '50%',
          transform: 'translateY(-50%) scale(0.5)',
          width: 18,
          height: 18,
          zIndex: 2,
          opacity: 0,
          transition: 'all 0.5s cubic-bezier(0.68, -0.15, 0.32, 1.15)',
        }}
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
      </svg>
    </button>
  );
}

export default ThemeToggle;
